import { documentToPlainText } from "@tribunalea/document-editor/document";

const SEARCH_STOP_WORDS = new Set([
  "a",
  "al",
  "anterior",
  "de",
  "del",
  "el",
  "en",
  "in",
  "informe",
  "la",
  "last",
  "los",
  "mi",
  "mis",
  "my",
  "our",
  "redaccion",
  "report",
  "the",
  "ultimo",
  "ultima",
]);

export function normalizeDraftSearchText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es-AR")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function searchTokens(value) {
  const tokens = normalizeDraftSearchText(value).split(" ").filter(Boolean);
  const meaningful = tokens.filter((token) => (
    token.length > 1 && !SEARCH_STOP_WORDS.has(token)
  ));
  return meaningful.length ? [...new Set(meaningful)] : [...new Set(tokens)];
}

function trigrams(value) {
  const normalized = `  ${normalizeDraftSearchText(value)} `;
  if (normalized.length < 3) return new Set([normalized]);
  const values = new Set();
  for (let index = 0; index <= normalized.length - 3; index += 1) {
    values.add(normalized.slice(index, index + 3));
  }
  return values;
}

function diceSimilarity(left, right) {
  const leftValues = trigrams(left);
  const rightValues = trigrams(right);
  if (!leftValues.size || !rightValues.size) return 0;
  let intersection = 0;
  leftValues.forEach((value) => {
    if (rightValues.has(value)) intersection += 1;
  });
  return (2 * intersection) / (leftValues.size + rightValues.size);
}

function editDistance(left, right) {
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    let diagonal = previous[0];
    previous[0] = leftIndex;
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      const above = previous[rightIndex];
      previous[rightIndex] = left[leftIndex - 1] === right[rightIndex - 1]
        ? diagonal
        : Math.min(diagonal, above, previous[rightIndex - 1]) + 1;
      diagonal = above;
    }
  }
  return previous[right.length];
}

function tokenSimilarity(left, right) {
  if (left === right) return 1;
  if (left.startsWith(right) || right.startsWith(left)) return 0.9;
  const longest = Math.max(left.length, right.length);
  if (longest < 4) return 0;
  return 1 - editDistance(left, right) / longest;
}

function bestTokenSimilarity(token, candidates) {
  return candidates.reduce(
    (best, candidate) => Math.max(best, tokenSimilarity(token, candidate)),
    0,
  );
}

function scoreDraft(draft, query) {
  const normalizedQuery = normalizeDraftSearchText(query);
  const title = normalizeDraftSearchText(draft.title);
  const content = normalizeDraftSearchText(documentToPlainText(draft.document));
  const queryTokens = searchTokens(normalizedQuery);
  if (!normalizedQuery || !queryTokens.length) return 1;

  let score = 0;
  if (title === normalizedQuery) score += 1_000;
  else if (title.includes(normalizedQuery)) score += 480;
  else if (content.includes(normalizedQuery)) score += 180;

  const titleTokens = searchTokens(title);
  const contentTokens = searchTokens(content).slice(0, 500);
  let matched = 0;
  queryTokens.forEach((token) => {
    const titleMatch = bestTokenSimilarity(token, titleTokens);
    const contentMatch = bestTokenSimilarity(token, contentTokens);
    if (titleMatch >= 0.66 || contentMatch >= 0.78) matched += 1;
    if (titleMatch >= 0.66) score += titleMatch * 95;
    if (contentMatch >= 0.78) score += contentMatch * 24;
  });
  score += (matched / queryTokens.length) * 160;
  score += diceSimilarity(title, normalizedQuery) * 180;
  return score;
}

export function searchJustinianoDrafts(drafts, query, { limit = 20 } = {}) {
  const normalizedQuery = normalizeDraftSearchText(query);
  if (!normalizedQuery) {
    return [...(drafts || [])]
      .sort((left, right) => (
        new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime()
      ))
      .slice(0, limit);
  }
  return (drafts || [])
    .map((draft) => ({ draft, score: scoreDraft(draft, normalizedQuery) }))
    .filter(({ score }) => score >= 95)
    .sort((left, right) => (
      right.score - left.score
      || new Date(right.draft.updatedAt).getTime()
        - new Date(left.draft.updatedAt).getTime()
    ))
    .slice(0, limit)
    .map(({ draft }) => draft);
}

export function resolveJustinianoDraft(drafts, { draftId, query } = {}) {
  const exact = (drafts || []).find(({ id }) => id === draftId);
  if (exact) return exact;
  return searchJustinianoDrafts(drafts, query, { limit: 1 })[0] || null;
}

export function searchRecentSimilarDrafts(drafts, query, {
  excludeDraftId = null,
  limit = 3,
  maxTextLength = 8_000,
} = {}) {
  const eligible = (drafts || []).filter((draft) => (
    draft?.id !== excludeDraftId
    && Boolean(documentToPlainText(draft?.document).trim())
  ));
  const recent = [...eligible].sort((left, right) => (
    new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime()
  ));
  const similar = searchJustinianoDrafts(eligible, query, { limit });
  const selected = [];
  const seen = new Set();
  for (const draft of [...similar, ...recent]) {
    if (seen.has(draft.id)) continue;
    seen.add(draft.id);
    selected.push(draft);
    if (selected.length >= limit) break;
  }
  return selected.map((draft) => ({
    id: draft.id,
    title: String(draft.title || "").trim() || "Redacción sin título",
    text: documentToPlainText(draft.document).slice(0, maxTextLength),
    updatedAt: draft.updatedAt,
  }));
}
